import React, { useState, useEffect } from 'react';
import { Folder, Upload, FileText, Image as ImageIcon, Download, Trash2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';
import Layout from '../../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Vault() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/inventions-os/files`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/api/inventions-os/files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('File uploaded successfully');
      loadFiles();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType === 'application/pdf') return FileText;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-muted-foreground">Loading files...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Folder className="h-8 w-8 text-primary" />
              Vault
            </h1>
            <p className="text-muted-foreground mt-2">
              All your uploaded documents and images
            </p>
          </div>
          <label className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors cursor-pointer">
            <Upload className="h-5 w-5" />
            <span className="hidden md:inline">Upload File</span>
            <span className="md:hidden">Upload</span>
            <input
              type="file"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              accept="image/*,application/pdf,.pdf"
            />
          </label>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="glass-card p-6">
            <div className="text-3xl font-bold text-white">{files.length}</div>
            <div className="text-sm text-muted-foreground mt-1">Total Files</div>
          </div>
          <div className="glass-card p-6">
            <div className="text-3xl font-bold text-white">
              {files.filter(f => f.mime_type?.startsWith('image/')).length}
            </div>
            <div className="text-sm text-muted-foreground mt-1">Images</div>
          </div>
          <div className="glass-card p-6">
            <div className="text-3xl font-bold text-white">
              {files.filter(f => f.mime_type === 'application/pdf').length}
            </div>
            <div className="text-sm text-muted-foreground mt-1">Documents</div>
          </div>
        </div>

        {/* Files List */}
        {files.length === 0 ? (
          <div className="glass-card p-12 text-center border-dashed">
            <Folder className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-2">No files yet</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Upload PDFs and images to attach them to your inventions
            </p>
            <label className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer">
              <Upload className="h-5 w-5" />
              Upload Your First File
              <input
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                accept="image/*,application/pdf,.pdf"
              />
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {files.map((file) => {
              const FileIcon = getFileIcon(file.mime_type);
              return (
                <div
                  key={file.file_id}
                  className="glass-card p-6 hover:border-primary/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/20 rounded-lg">
                      <FileIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold truncate">
                        {file.original_filename}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatFileSize(file.file_size)}
                      </p>
                      {file.attached_to_id && (
                        <div className="flex items-center gap-1 mt-2">
                          <Paperclip className="h-3 w-3 text-primary" />
                          <span className="text-xs text-primary">Attached to invention</span>
                        </div>
                      )}
                      {file.processed && file.extracted_text && (
                        <p className="text-xs text-green-400 mt-1">
                          ✓ Text extracted
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {new Date(file.uploaded_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <a
                        href={`${API_URL}/api/inventions-os/files/${file.file_id}`}
                        download={file.original_filename}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="h-4 w-4 text-white" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
